/**
 * EntityListBody — main list content area (table/cards + optional detail panel).
 * Extracted from ItemListView / EntityListPage; instantiate for any entity list.
 */

import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { useMobileExperience } from '../../mobile/featureFlag';
import { BottomSheet } from '../../mobile/BottomSheet';

export interface EntityListBodyProps {
  children: ReactNode;
  /** Content above the flex row (bulk actions bar, etc.) */
  before?: ReactNode;
  detailPanel?: ReactNode;
  detailPanelOpen?: boolean;
  detailPanelWidth?: number;
  /** Title shown in the mobile bottom sheet when detailPanel is open. */
  detailPanelTitle?: string;
  /** Called when the mobile bottom sheet is dismissed. */
  onDetailPanelClose?: () => void;
  className?: string;
}

export function EntityListBody({
  children,
  before,
  detailPanel,
  detailPanelOpen = false,
  detailPanelWidth = 380,
  detailPanelTitle,
  onDetailPanelClose,
  className,
}: EntityListBodyProps) {
  const isMobile = useMobileExperience();

  if (isMobile && detailPanel != null) {
    return (
      <>
        {before}
        <div className={`flex-1 flex flex-col overflow-hidden min-h-0 ${className ?? ''}`}>
          {children}
        </div>
        <BottomSheet
          open={detailPanelOpen}
          onClose={() => onDetailPanelClose?.()}
          title={detailPanelTitle}
          maxHeightClass="max-h-[92vh]"
        >
          {detailPanelOpen ? detailPanel : null}
        </BottomSheet>
      </>
    );
  }

  return (
    <>
      {before}
      <div className={`flex-1 flex overflow-hidden min-h-0 ${className ?? ''}`}>
        {children}
        {detailPanel != null && (
          <div
            className={`flex-shrink-0 flex flex-col border-l border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden transition-all duration-200 ${
              detailPanelOpen ? '' : 'w-0'
            }`}
            style={detailPanelOpen ? { width: detailPanelWidth } : undefined}
          >
            {detailPanelOpen && (
              <>
                {/* Own strip, never the panel's own corner — every panel already
                    reserves that corner for its own action buttons (edit/delete,
                    save/cancel). Without this, there is no way to dismiss the
                    panel except selecting a different row. Gated on
                    onDetailPanelClose being provided — callers whose panel
                    already has its own working close (e.g. ItemQuickPanel)
                    don't pass it, and shouldn't get a second, inert button. */}
                {onDetailPanelClose && (
                  <div className="flex-shrink-0 h-9 flex items-center justify-end px-2 border-b border-[var(--rule)] dark:border-[var(--rule)]">
                    <button
                      type="button"
                      onClick={() => onDetailPanelClose()}
                      aria-label="Close detail panel"
                      title="Close"
                      className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] dark:hover:text-[var(--text-pri)]"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <div className="flex-1 overflow-auto">{detailPanel}</div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
